import React from 'react';

import { Button, Pagination, Select, Table, Toolbar } from '../../../design-system/components/desktop';
import { ConsoleEmpty, ConsoleError, StatusPill } from '../../../design-system/patterns/console';
import { OrderItemsCell, parseRand, shortOrderId } from '../../Dashboard/dashboard.parts';
import { formatCurrency } from '../../../utils/common';

import '../../Dashboard/dashboard.css';

/**
 * The seller's orders, the view. Pure.
 *
 * ORDERS ARE MANAGING, SO THIS IS A TABLE. A row opens the order drawer, which
 * is the A3 shape: title, toolbar, table, pagination, row opens a detail
 * surface.
 *
 * TWO STATUS COLUMNS, TWO PILLS, ONE VOCABULARY. Both go through `StatusPill`,
 * which reads `utils/orderStatus.js` and returns four tones plus primary on
 * `approved`. "Shipped" is not one of the values that file knows and is not
 * introduced here.
 *
 * WHAT DIED: `GenericTable`, `TableFilters`, the `StatusBadge` component, the
 * hand-rolled skeleton, and the Eye button carrying eleven inline styles
 * including a `#282E3D` border and a `#9BA3AF` foreground, both of which were
 * dark-theme values shipped into light theme. The row action is a real ghost
 * button in `.dsk-table__rowactions`, revealed on hover OR focus-within, so it
 * is reachable from the keyboard.
 */
export function OrderTableView({
  rows = [],
  loading = false,
  error,
  onRetry,

  search = '',
  onSearchChange,
  status = '',
  onStatusChange,
  statusOptions = [],

  page = 1,
  pageCount = 1,
  pageSize = 10,
  total = 0,
  onPageChange,

  onView,
  drawer = null,
}) {
  const filtered = Boolean(search || status);

  const columns = [
    {
      key: 'order_id',
      header: 'Order',
      render: (row) => shortOrderId(row.order_id),
    },
    {
      key: 'items',
      header: 'Items',
      render: (row) => <OrderItemsCell items={row.items} />,
    },
    {
      key: 'total',
      header: 'Total',
      numeric: true,
      render: (row) => formatCurrency(parseRand(row.total)),
    },
    {
      key: 'order_status',
      header: 'Payment',
      render: (row) => <StatusPill status={row.order_status} />,
    },
    {
      key: 'shipment_status',
      header: 'Shipment',
      render: (row) =>
        row.shipment_status ? <StatusPill status={row.shipment_status} /> : 'Not booked yet',
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        data={error ? [] : rows}
        rowKey={(row, index) => row.order_id || index}
        loading={loading}
        onRowClick={(row) => onView && onView(row)}
        emptyState={
          error ? (
            <ConsoleError error={error} onRetry={onRetry} title="Could not load your orders" />
          ) : (
            <ConsoleEmpty
              filtered={filtered}
              title="No orders yet"
              text="Orders buyers place on your listings appear here."
              filteredText="No order on your listings matches that search or status. Clear one of them and they come back."
              onClear={() => {
                if (onSearchChange) onSearchChange('');
                if (onStatusChange) onStatusChange('');
              }}
            />
          )
        }
        rowActions={(row) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              onView(row);
            }}
          >
            View order
          </Button>
        )}
        toolbar={
          <Toolbar
            search={search}
            onSearchChange={onSearchChange}
            searchPlaceholder="Filter by order or card"
            searchLabel="Filter your orders"
            count={loading ? undefined : `${total} ${total === 1 ? 'order' : 'orders'}`}
            filters={
              <div className="dsa-dash__filters">
                <Select
                  options={statusOptions}
                  value={status}
                  onChange={onStatusChange}
                  placeholder="All statuses"
                  label="Filter by order status"
                />
              </div>
            }
          />
        }
        pagination={
          pageCount > 1 ? (
            <Pagination
              page={page}
              pageCount={pageCount}
              pageSize={pageSize}
              totalItems={total}
              itemNoun="orders"
              onPageChange={onPageChange}
            />
          ) : null
        }
      />
      {drawer}
    </>
  );
}

export default OrderTableView;
